import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../config';
import { contactAPI } from '../services/api';
import { listCourses } from '../services/courses';
import { useTurnstile } from '../hooks/useTurnstile';
import { useAuth } from '../context/AuthContext';
import './ContactForm.css';

const ContactForm = () => {
  const { user, isAuthenticated } = useAuth();

  const [courseOptions, setCourseOptions] = useState([]);

  useEffect(() => {
    listCourses()
      .then((res) => {
        if (res.data?.success) {
          setCourseOptions((res.data.data || []).map((c) => c.title));
        }
      })
      .catch(() => {});
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    enquiryType: '',
    courseSelection: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // Email verification states
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [otpStatus, setOtpStatus] = useState(null);

  const {
    getTurnstileToken,
    resetTurnstile,
    turnstileRef,
    turnstileStatus,
    isTurnstileConfigured,
  } = useTurnstile();

  const turnstileBlocking = isTurnstileConfigured && turnstileStatus !== 'ready' && turnstileStatus !== 'error';

  // Pre-fill and auto-verify when user is logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      const rawPhone = (user.phone || '').replace(/^\+91/, '');
      setFormData((prev) => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
        phone: rawPhone || prev.phone,
      }));
      if (user.email) setEmailVerified(true);
    }
  }, [isAuthenticated, user]);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleChange = (e) => {
    const newFormData = {
      ...formData,
      [e.target.name]: e.target.value
    };
    setFormData(newFormData);
    
    // Reset email verification if email changes
    if (e.target.name === 'email') {
      setEmailVerified(false);
      setOtpSent(false);
      setOtpCode('');
      setOtpStatus(null);
    }
  };

  const handleSendOTP = async () => {
    if (!formData.email) {
      setOtpStatus({ type: 'error', message: 'Please enter your email address first' });
      return;
    }

    if (!isValidEmail(formData.email)) {
      setOtpStatus({ type: 'error', message: 'Please enter a valid email address' });
      return;
    }

    setIsSendingOTP(true);
    setOtpStatus(null);

    try {
      const turnstileToken = await getTurnstileToken();
      const response = await contactAPI.sendEmailOTP({
        email: formData.email,
        name: formData.name || 'User',
        turnstileToken
      });

      if (response.data.Status === 'Success') {
        setOtpSent(true);
        setOtpStatus({ type: 'success', message: 'Verification code sent to your email!' });
        resetTurnstile();
      }
    } catch (error) {
      if (error.isTurnstileError) {
        setOtpStatus({
          type: 'error',
          message: 'Verification failed. Please wait a moment then try again.'
        });
      } else if (error.response?.status === 429) {
        setOtpStatus({
          type: 'error',
          message: error.response?.data?.Message?.Message || 'Too many requests. Please try again later.'
        });
      } else {
        setOtpStatus({
          type: 'error',
          message: error.response?.data?.Message?.Message || 'Failed to send verification code. Please try again.'
        });
      }
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setOtpStatus({ type: 'error', message: 'Please enter the 6-digit verification code' });
      return;
    }

    setIsVerifyingOTP(true);
    setOtpStatus(null);

    try {
      const turnstileToken = await getTurnstileToken();
      const response = await contactAPI.verifyEmailOTP({
        email: formData.email,
        code: otpCode,
        turnstileToken
      });

      if (response.data.Message?.Verified === 'True') {
        setEmailVerified(true);
        setOtpStatus({ type: 'success', message: 'Email verified successfully!' });
        resetTurnstile();
      }
    } catch (error) {
      if (error.isTurnstileError) {
        setOtpStatus({
          type: 'error',
          message: 'Verification failed. Please wait a moment then try again.'
        });
      } else {
        setOtpStatus({
          type: 'error',
          message: error.response?.data?.Message?.Message || 'Invalid verification code. Please try again.'
        });
      }
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailVerified) {
      setSubmitStatus({
        type: 'error',
        message: 'Please verify your email address before submitting the form.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const turnstileToken = await getTurnstileToken();
      let builtSubject = '';
      if (formData.enquiryType === 'course') {
        builtSubject = formData.courseSelection
          ? `Course Enquiry: ${formData.courseSelection}`
          : 'Course Enquiry';
      } else if (formData.enquiryType === 'college') {
        builtSubject = 'College & University Programs';
      } else {
        builtSubject = 'General Enquiry';
      }
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone ? `+91${formData.phone}` : '',
        subject: builtSubject,
        message: formData.message,
        verifiedEmail: formData.email,
        turnstileToken
      };
      const response = await axios.post(`${apiBaseUrl}/contact/sendcontactmessage`, payload);

      if (response.data.success) {
        resetTurnstile();
        setSubmitStatus({ type: 'success', message: response.data.message });
        // Keep name/email/phone pre-filled for logged-in users; only clear message fields
        setFormData({
          name: isAuthenticated && user?.name ? user.name : '',
          email: isAuthenticated && user?.email ? user.email : '',
          phone: isAuthenticated && user?.phone ? user.phone.replace(/^\+91/, '') : '',
          enquiryType: '',
          courseSelection: '',
          message: ''
        });
        if (!isAuthenticated) {
          setEmailVerified(false);
          setOtpSent(false);
          setOtpCode('');
          setOtpStatus(null);
        }
      }
    } catch (error) {
      if (error.isTurnstileError) {
        setSubmitStatus({
          type: 'error',
          message: 'Verification failed. Please wait a moment then try again.'
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: error.response?.data?.message || 'Failed to send message. Please try again.'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit}>

      {submitStatus && (
        <div className={`form-message ${submitStatus.type}`}>
          {submitStatus.message}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="name">Name *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Your full name"
          readOnly={isAuthenticated && !!user?.name}
          className={isAuthenticated && user?.name ? 'verified' : ''}
        />
      </div>

      <div className="form-group">
        <label htmlFor="email">Email *</label>
        <div className="email-verification-wrapper">
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="you@email.com"
            disabled={emailVerified}
            className={emailVerified ? 'verified' : ''}
          />
          {emailVerified && (
            <span className="verified-badge">✓ Verified</span>
          )}
        </div>
        
        {!emailVerified && formData.email && isValidEmail(formData.email) && (
          <div className="otp-section">
            {!otpSent ? (
              <button
                type="button"
                onClick={handleSendOTP}
                disabled={isSendingOTP || turnstileBlocking}
                className="btn-otp"
              >
                {isSendingOTP ? 'Sending…' : 'Send Verification Code'}
              </button>
            ) : (
              <div className="otp-input-group">
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  maxLength="6"
                  className="otp-input"
                />
                <button
                  type="button"
                  onClick={handleVerifyOTP}
                  disabled={isVerifyingOTP || otpCode.length !== 6 || turnstileBlocking}
                  className="btn-verify"
                >
                  {isVerifyingOTP ? 'Verifying…' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={handleSendOTP}
                  disabled={isSendingOTP}
                  className="btn-resend"
                >
                  {isSendingOTP ? 'Sending…' : 'Resend'}
                </button>
              </div>
            )}
            {otpStatus && (
              <div className={`otp-message ${otpStatus.type}`}>
                {otpStatus.message}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="phone">Phone</label>
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
          <span style={{
            display: 'flex', alignItems: 'center', padding: '0 12px',
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            borderRight: 'none', borderRadius: '8px 0 0 8px',
            fontSize: 14, color: 'var(--text-2)', whiteSpace: 'nowrap', userSelect: 'none'
          }}>+91</span>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
            placeholder="Your phone number"
            maxLength={10}
            inputMode="numeric"
            style={{ borderRadius: '0 8px 8px 0', flex: 1 }}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="enquiryType">Subject *</label>
        <select
          id="enquiryType"
          name="enquiryType"
          value={formData.enquiryType}
          onChange={handleChange}
          className="form-select"
          required
        >
          <option value="">Select a subject</option>
          <option value="course">Course Enquiry</option>
          <option value="college">College &amp; University Programs</option>
          <option value="other">Others</option>
        </select>
      </div>

      {formData.enquiryType === 'course' && (
        <div className="form-group">
          <label htmlFor="courseSelection">Which course?</label>
          <select
            id="courseSelection"
            name="courseSelection"
            value={formData.courseSelection}
            onChange={handleChange}
            className="form-select"
          >
            <option value="">Select a course</option>
            {courseOptions.map((title) => (
              <option key={title} value={title}>{title}</option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="message">Message *</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows="4"
          placeholder={
            formData.enquiryType === 'course' ? 'Tell us about your learning goals or any questions about the course…'
            : formData.enquiryType === 'college' ? 'Tell us about your institution, the program you have in mind, or any questions…'
            : 'Tell us how we can help you…'
          }
        />
      </div>

      <div className="turnstile-form-group turnstile-managed-mount">
        <span className="visually-hidden">
          Anti-bot verification by Cloudflare. A verification step may appear if your request looks unusual.
        </span>
        <div ref={turnstileRef} className="turnstile-widget-wrap" />
      </div>

      <button
        type="submit"
        className="btn-submit btn-send-message"
        disabled={isSubmitting || !emailVerified || turnstileBlocking}
      >
        {isSubmitting ? 'Sending…' : 'Send Message'}
        <svg className="btn-send-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="22" y1="2" x2="11" y2="13"/>
          <polygon points="22 2 15 22 11 13 2 9 22 2"/>
        </svg>
      </button>
    </form>
  );
};

export default ContactForm;
